import React, { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import httpClient from "../../components/helpers/httpClient";
import { toast } from "react-toastify";
import "../mobile.css";
import Dropdown from "../../components/Dropdown";
import CheckBox from "../../components/CheckBox";
import { DateField } from "@mui/x-date-pickers";
import { formatDate, parseDateValue, serializeDate } from "../../components/helpers/formatDate";

// Mobile Components
import {
  MobileField,
  MobileTagDropdown,
  MobileNoteItem,
  MobileNoteDetail,
  MobileCompanyItem,
  MobilePropertyItem,
  MobileRentalItem,
} from "../components";

const SECTIONS = [
  { id: "details", label: "Details" },
  { id: "properties", label: "Properties" },
  { id: "tags", label: "Tags" },
  { id: "notes", label: "Notes" },
  { id: "companies", label: "Companies" },
];

export default function MobileContactDrawer({ open, contactID, onClose }) {
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState(null);
  const [edit, setEdit] = useState(false);
  const [formData, setFormData] = useState({});
  const [contactsList, setContactsList] = useState([]);
  const [activeSection, setActiveSection] = useState("details");
  
  // Additional data for other sections
  const [tags, setTags] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [notes, setNotes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rentalContracts, setRentalContracts] = useState([]);
  const [propertiesTab, setPropertiesTab] = useState("properties");
  const [selectedNote, setSelectedNote] = useState(null);

  const title = useMemo(() => {
    const first = contact?.first_name || "";
    const last = contact?.last_name || "";
    const full = `${first} ${last}`.trim();
    return full || (contactID ? `Contact #${contactID}` : "Contact");
  }, [contact, contactID]);

  const fetchContact = async () => {
    setLoading(true);
    try {
      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/contacts/get?contactID=${contactID}`
      );
      const data = response?.data ?? null;
      setContact(data);
      if (data) {
        setFormData({
          ...data,
          birthday: getBirthdayStateValue(data.birthday),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFullContacts = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/contacts/get");
    setContactsList(response.data);
  };

  const fetchTags = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/tags/get/${contactID}?type=contact`
      );
      setTags(response.data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchTagsList = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/tags/get");
      setTagsList(response.data || []);
    } catch (error) {
      console.error("Error fetching tags list:", error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/notes/get/${contactID}?type=contact`
      );
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/company-contacts/get/${contactID}?type=companies`
      );
      setCompanies(response.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/properties/properties-of-contact?contactID=${contactID}`
      );
      setProperties(response.data || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
      setProperties([]);
    }
  };

  const fetchRentalContracts = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/rental-contracts/of-contact?contactID=${contactID}&detailed=true`
      );
      setRentalContracts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching rental contracts:", error);
      setRentalContracts([]);
    }
  };

  useEffect(() => {
    if (!open || !contactID) return;
    setActiveSection("details");
    setEdit(false);
    fetchContact();
    fetchFullContacts();
    fetchTags();
    fetchTagsList();
    fetchNotes();
    fetchCompanies();
    fetchProperties();
    fetchRentalContracts();
  }, [open, contactID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSetState = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getBirthdayStateValue = (value) => {
    return parseDateValue(value);
  };

  const validateEmailValue = (value) => {
    if (!value) return true;
    return /\S+@\S+\.\S+/.test(String(value).trim());
  };

  const renderEmailValue = (email) => {
    if (!email) return "";

    return (
      <div className="mobile-email-value">
        <span>{email}</span>
        <a href={`mailto:${email}`} className="mobile-email-action" aria-label={`Send email to ${email}`}>
          <MailOutlineIcon fontSize="small" />
        </a>
      </div>
    );
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (!validateEmailValue(payload.email) || !validateEmailValue(payload.home_email)) {
        toast.error("Please enter a valid email address");
        return;
      }

      payload.birthday = serializeDate(payload.birthday);
      
      await toast.promise(
        httpClient.put(import.meta.env.VITE_API_URL + `/api/contact/update?contactID=${contactID}`, payload),
        {
          pending: "Updating contact...",
          success: "Contact updated 👍",
          error: "Failed to update contact 😢",
        }
      );
      setEdit(false);
      fetchContact();
    } catch (error) {
      console.error("Error saving contact:", error);
    }
  };

  const handleAddTag = async (tagID) => {
    try {
      await httpClient.put(
        import.meta.env.VITE_API_URL + `/api/tags/add/${contactID}?tagID=${tagID}&type=contact`
      );
      fetchTags();
    } catch (error) {
      console.error("Error adding tag:", error);
    }
  };

  const handleRemoveTag = async (tagID) => {
    try {
      await httpClient.delete(
        import.meta.env.VITE_API_URL + `/api/tags/remove/${contactID}?tagID=${tagID}&type=contact`
      );
      fetchTags();
    } catch (error) {
      console.error("Error removing tag:", error);
    }
  };

  const handleCreateTag = async (tagValue) => {
    try {
      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/tags/create?tagValue=${tagValue}`
      );
      const newTag = response.data;
      await handleAddTag(newTag.id);
      fetchTagsList();
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const renderSectionContent = () => {
    if (loading) {
      return <div style={{ color: "var(--secondairy-medium-gray)", padding: "20px", textAlign: "center" }}>Loading details...</div>;
    }
    
    if (!contact && activeSection === "details") {
      return <div style={{ color: "var(--secondairy-medium-gray)", padding: "20px", textAlign: "center" }}>No contact data found.</div>;
    }

    switch (activeSection) {
      case "details":
        return renderDetailsSection();
      case "properties":
        return renderPropertiesSection();
      case "tags":
        return renderTagsSection();
      case "notes":
        return renderNotesSection();
      case "companies":
        return renderCompaniesSection();
      default:
        return null;
    }
  };

  const renderDetailsSection = () => (
    <>
      <MobileField 
        label="Title" 
        value={contact.title} 
        edit={edit}
        component={
          <Dropdown
            listArray={["Heer", "Mevrouw", "Juffrouw", "Professor", "Dokter", "Meester"]}
            state={formData.title}
            setState={(val) => handleSetState("title", val)}
            width="100%"
            height="40px"
          />
        }
      />
      <MobileField 
        label="First Name" 
        value={contact.first_name} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Last Name" 
        value={contact.last_name} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Married?" 
        value={contact.is_married ? "Yes" : "No"} 
        edit={edit}
        component={
          <CheckBox
            state={formData.is_married}
            setState={(val) => handleSetState("is_married", val)}
            text=""
            height="24px"
          />
        }
      />
      {formData.is_married && (
        <MobileField 
          label="Partner" 
          value={contact.partner ? `${contact.partner.first_name} ${contact.partner.last_name}` : ""} 
          edit={edit}
          component={
            <Dropdown
              listArray={contactsList}
              state={formData.partner}
              setState={(val) => handleSetState("partner", val)}
              placeholder="Select partner"
              width="100%"
              height="40px"
              searchBar={true}
            />
          }
        />
      )}
      <MobileField 
        label="Business email" 
        value={renderEmailValue(contact.email)} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="email"
            value={formData.email || ""}
            onChange={handleInputChange}
            type="email"
          />
        }
      />
      <MobileField 
        label="Home email" 
        value={renderEmailValue(contact.home_email)} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="home_email"
            value={formData.home_email || ""}
            onChange={handleInputChange}
            type="email"
          />
        }
      />
      <MobileField 
        label="Mobile" 
        value={contact.mobile_phone} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="mobile_phone"
            value={formData.mobile_phone || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Business Ph" 
        value={contact.business_phone} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="business_phone"
            value={formData.business_phone || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Home Ph" 
        value={contact.home_phone} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="home_phone"
            value={formData.home_phone || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Address" 
        value={contact.address} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="address"
            value={formData.address || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Postal" 
        value={contact.postal} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="postal"
            value={formData.postal || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="City" 
        value={contact.city} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="city"
            value={formData.city || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Province" 
        value={contact.province} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="province"
            value={formData.province || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Country" 
        value={contact.country} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="country"
            value={formData.country || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Birthday" 
        value={formatDate(contact.birthday)}
        edit={edit}
        component={
          <DateField
            className="date-picker"
            format="DD-MM-YYYY"
            value={formData.birthday}
            onChange={(val) => handleSetState("birthday", val)}
            sx={{ width: '100%', background: 'white', borderRadius: '12px' }}
          />
        }
      />
      <MobileField 
        label="Language" 
        value={contact.language} 
        edit={edit}
        component={
          <Dropdown
            listArray={["Dutch", "English", "French", "Spanish", "Other"]}
            state={formData.language}
            setState={(val) => handleSetState("language", val)}
            width="100%"
            height="40px"
          />
        }
      />
    </>
  );

  const renderPropertiesSection = () => (
    <div className="mobile-section-content">
      <div className="mobile-tab-switcher">
        <button 
          className={`mobile-tab-btn ${propertiesTab === "properties" ? "active" : ""}`}
          onClick={() => setPropertiesTab("properties")}
        >
          Properties
        </button>
        <button 
          className={`mobile-tab-btn ${propertiesTab === "rentals" ? "active" : ""}`}
          onClick={() => setPropertiesTab("rentals")}
        >
          Rentals
        </button>
      </div>

      {propertiesTab === "properties" ? (
        properties.length === 0 ? (
          <div className="mobile-empty-state">No properties owned by this contact.</div>
        ) : (
          <div className="mobile-items-list">
            {properties.map((property, index) => (
              <MobilePropertyItem key={index} property={property} onClick={() => {}} />
            ))}
          </div>
        )
      ) : (
        rentalContracts.length === 0 ? (
          <div className="mobile-empty-state">No rental contracts linked to this contact.</div>
        ) : (
          <div className="mobile-items-list">
            {rentalContracts.map((contract, index) => (
              <MobileRentalItem 
                key={contract.id || index} 
                contract={contract} 
                entityID={contactID}
                entityType="contact"
                onClick={() => {}} 
              />
            ))}
          </div>
        )
      )}
    </div>
  );

  const renderTagsSection = () => (
    <div className="mobile-section-content">
      <MobileTagDropdown
        tags={tags}
        tagsList={tagsList}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        onCreateTag={handleCreateTag}
        placeholder="Search or add tags..."
      />
    </div>
  );

  const renderNotesSection = () => (
    <div className="mobile-section-content">
      {selectedNote ? (
        <MobileNoteDetail 
          note={selectedNote} 
          onBack={() => setSelectedNote(null)} 
        />
      ) : (
        <>
          {notes.length === 0 ? (
            <div className="mobile-empty-state">No notes for this contact.</div>
          ) : (
            <div className="mobile-items-list">
              {notes.map((note, index) => (
                <MobileNoteItem 
                  key={index} 
                  note={note} 
                  onClick={() => setSelectedNote(note)} 
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderCompaniesSection = () => (
    <div className="mobile-section-content">
      {companies.length === 0 ? (
        <div className="mobile-empty-state">No companies linked to this contact.</div>
      ) : (
        <div className="mobile-items-list">
          {companies.map((company, index) => (
            <MobileCompanyItem 
              key={index} 
              company={company} 
              onClick={() => {}} 
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "min(95vw, 450px)",
          backgroundColor: "var(--mobile-bg)",
        },
      }}
    >
      <div className="mobile-drawer__header">
        <div style={{ fontWeight: 700, fontSize: 18, flex: 1 }}>{title}</div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {activeSection === "details" && (
            !edit ? (
              <IconButton onClick={() => setEdit(true)} aria-label="Edit">
                <EditIcon />
              </IconButton>
            ) : (
              <IconButton onClick={handleSave} aria-label="Save" color="primary">
                <SaveIcon />
              </IconButton>
            )
          )}
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="mobile-section-tabs">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            className={`mobile-section-tab ${activeSection === section.id ? "active" : ""}`}
            onClick={() => {
              setActiveSection(section.id);
              setSelectedNote(null);
            }}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="mobile-drawer__content">
        {renderSectionContent()}
      </div>
    </Drawer>
  );
}
